import { Loader } from '@modules/common/components/Loader';
import { useNotificationContext } from '@modules/common/components/Notification/NotificationContext';
import {
  NAV_PATHS,
  NOTIFICATION_TIMEOUT_LONG_6S,
  RETURN_PATH_QUERY_PARAM,
} from '@modules/common/constants';
import { PersonRounded } from '@mui/icons-material';
import { LoadingButton } from '@mui/lab';
import {
  Box,
  Button,
  Container,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useGetUserByEmailLazyQuery } from 'generated/graphql';
import {
  ChangeEvent,
  FormEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import AuthService from 'services/AuthService';
import { createAnalyticsTracker } from 'v2/analytics/events';
import { useMustGetAmplifyUser } from 'v2/core/clients/authAmplify/hooks';
import ErrorCode from 'v2/core/errors/codes';
import { isErrorCode } from 'v2/core/errors/errors';
import useCreateUserOnEdgeServer from 'v2/core/repository/edge-server/user/useCreateUserOnEdgeServer';
import { toAuthError } from 'v2/core/services/auth/errors';
import { useProcessInviteCodeForAuth } from 'v2/core/services/auth/useProcessInviteCodeForAuth';
import { AuthenticationCardWrapper } from '../AuthenticationCardWrapper';

const trackOnboardingEvents = createAnalyticsTracker('onboarding');

export const Onboarding = () => {
  const amplifyUser = useMustGetAmplifyUser();

  const [loader, setLoader] = useState(false);
  const [userInfo, setUserInfo] = useState(amplifyUser);
  const hasInitiatedUserCreation = useRef(false);

  const navigate = useNavigate();
  const location = useLocation();

  const [getUserByEmail] = useGetUserByEmailLazyQuery();
  const { showErrorNotification } = useNotificationContext();

  const { firstName, lastName } = userInfo;

  const goToReturnRoute = useCallback(() => {
    const searchParams = new URLSearchParams(location.search);

    const { [RETURN_PATH_QUERY_PARAM]: returnTo } =
      Object.fromEntries(searchParams);

    if (!returnTo || !returnTo.startsWith('/')) {
      navigate(NAV_PATHS.USER_ONBOARDING_WELCOME);
      return;
    }

    navigate({
      pathname: NAV_PATHS.USER_ONBOARDING_WELCOME,
      search: searchParams.toString(),
    });
  }, [location.search, navigate]);

  const onAPIError = useCallback(
    (err: Error) => {
      if (err.message.toLowerCase().includes('duplicate entry')) {
        navigate('/');
        return;
      }
      if (isErrorCode(err, ErrorCode.UserUnapproved)) {
        // TODO: Check why navigate('/login') doesn't work here
        window.location.href = '/login';
        return;
      }
      showErrorNotification({
        contentComponent: toAuthError(err).message,
        props: {
          timeout: NOTIFICATION_TIMEOUT_LONG_6S,
        },
      });
      // TODO: Check why navigate('/login') doesn't work here
      window.location.href = '/login';
    },
    [navigate, showErrorNotification],
  );

  const { processInviteCodeIfPresent } = useProcessInviteCodeForAuth({
    onError: onAPIError,
  });

  const { createUser: createUserMutation, error } = useCreateUserOnEdgeServer({
    payload: userInfo,
    onError: onAPIError,
  });

  const onCreateUser = useCallback(() => {
    goToReturnRoute();
    trackOnboardingEvents({
      eventName: 'Account Created on Enterpret',
      ...amplifyUser,
    });
    trackOnboardingEvents({
      eventName: 'Continue from On-boarding CTA',
      ...amplifyUser,
    });
  }, [amplifyUser, goToReturnRoute]);

  const createUser = useCallback(async () => {
    const tenantSlug: string = AuthService.getTenantSlug();

    setLoader(true);
    try {
      await createUserMutation();
      await processInviteCodeIfPresent();
      await getUserByEmail({
        variables: {
          email: amplifyUser.email,
          slug: tenantSlug,
        },
        onError: onAPIError,
      });
      onCreateUser();
      setLoader(false);
    } catch (e) {
      setLoader(false);
    }
  }, [
    amplifyUser.email,
    createUserMutation,
    getUserByEmail,
    onAPIError,
    onCreateUser,
    processInviteCodeIfPresent,
  ]);

  const onSubmit = async (event: FormEvent) => {
    event.preventDefault();
    createUser();
  };

  const onLogoutClick = () => {
    navigate('/logout');
  };

  const setAuthData = (e: ChangeEvent<HTMLInputElement>) => {
    const {
      target: { value, name },
    } = e;
    setUserInfo((userInfo) => ({
      ...userInfo,
      [name]: value,
    }));
  };

  const isUserInfoAlreadyEntered =
    amplifyUser.firstName && amplifyUser.lastName;

  const shouldAutoCreateUser = isUserInfoAlreadyEntered && !error;

  // If user info was already entered by the user on the previous auth screen, create the user directly
  useEffect(() => {
    if (!hasInitiatedUserCreation.current && shouldAutoCreateUser) {
      createUser();
      hasInitiatedUserCreation.current = true;
    }
  }, [createUser, shouldAutoCreateUser]);

  if (loader && !error) {
    return <Loader />;
  }

  if (shouldAutoCreateUser) {
    return null;
  }

  return (
    <Container maxWidth="xl">
      <Stack>
        <Stack alignItems="flex-end" position="relative" top="50px">
          <Button variant="outlined" onClick={onLogoutClick}>
            Logout
          </Button>
        </Stack>
        <AuthenticationCardWrapper width={600}>
          <Typography variant="body1">One more step</Typography>
          <Box component="form" mt={2} onSubmit={onSubmit}>
            <Stack spacing={3}>
              <TextField
                label="First Name"
                placeholder="Enter first name"
                onChange={setAuthData}
                name="firstName"
                value={firstName}
                required
                fullWidth
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PersonRounded color="disabled" />
                    </InputAdornment>
                  ),
                }}
              />
              <TextField
                label="Last Name"
                placeholder="Enter last name"
                onChange={setAuthData}
                name="lastName"
                value={lastName}
                required
                fullWidth
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PersonRounded color="disabled" />
                    </InputAdornment>
                  ),
                }}
              />
              <LoadingButton
                fullWidth
                variant="contained"
                type="submit"
                loading={loader}
              >
                Continue
              </LoadingButton>
            </Stack>
          </Box>
        </AuthenticationCardWrapper>
      </Stack>
    </Container>
  );
};
